import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { useAuth } from './AuthContext';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { can, currentUser, logout } = useAuth();
  /* 
   * CURRENTLY USING MOCK DATA
   * This dashboard displays static/mock data for demonstration.
   * When ready to connect to backend, uncomment the useEffect below
   * and the mock data will be replaced with real API calls.
   */
  
  // Uncomment this to fetch real data from backend:
  /*
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/api/dashboard/summary`);
        if (!response.ok) throw new Error('Failed to fetch dashboard data');
        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        console.error('Error fetching dashboard:', err);
      }
    };
    
    fetchDashboardData();
  }, []);
  */
  
  // Mock data - replace with API calls to your Flask backend
  const [dashboardData, setDashboardData] = useState({
    securityPosture: {
      currentMaturity: 2.4,
      targetMaturity: 3.5,
      lastAssessment: '2024-01-15',
      trend: 'improving', // improving, stable, declining
      categories: [
        { name: 'Identity & Access', score: 3.2, target: 4.0 },
        { name: 'Network Security', score: 2.8, target: 3.5 },
        { name: 'Data Protection', score: 2.1, target: 3.5 },
        { name: 'Incident Response', score: 1.9, target: 3.0 },
        { name: 'Vulnerability Mgmt', score: 2.6, target: 3.5 },
      ]
    },
    workflows: [
      { id: 1, name: 'Q1 2024 Risk Assessment', type: 'assessment', status: 'in_progress', progress: 65, dueDate: '2024-01-25' },
      { id: 2, name: 'Firewall Rule Audit', type: 'audit', status: 'in_progress', progress: 40, dueDate: '2024-01-30' },
      { id: 3, name: 'Network Topology Analysis', type: 'analysis', status: 'pending_review', progress: 90, dueDate: '2024-01-22' },
    ],
    recentFindings: [
      { id: 1, type: 'network_diagram', name: 'HQ Network Topology.png', uploadedAt: '2024-01-20 09:30', status: 'parsed', devices: 24, connections: 31 },
      { id: 2, type: 'config', name: 'core-router-01.conf', uploadedAt: '2024-01-19 16:45', status: 'parsed', issues: 3 },
      { id: 3, type: 'excel', name: 'Server Inventory Q1.xlsx', uploadedAt: '2024-01-19 14:20', status: 'parsed', assets: 147 },
      { id: 4, type: 'pdf', name: 'Security Policy 2024.pdf', uploadedAt: '2024-01-18 11:15', status: 'parsed', pages: 23 },
    ],
    riskMetrics: {
      criticalFindings: 12,
      highFindings: 34,
      mediumFindings: 67,
      openRemediations: 89,
      avgRemediationTime: 14 // days
    }
  });

  // Maturity level colors
  const getMaturityColor = (score) => {
    if (score >= 4.0) return 'text-emerald-600';
    if (score >= 3.0) return 'text-blue-600';
    if (score >= 2.0) return 'text-amber-600';
    return 'text-red-600';
  };

  const getMaturityBg = (score) => {
    if (score >= 4.0) return 'bg-emerald-50';
    if (score >= 3.0) return 'bg-blue-50';
    if (score >= 2.0) return 'bg-amber-50';
    return 'bg-red-50';
  };

  const getStatusColor = (status) => {
    const colors = {
      'in_progress': 'bg-blue-100 text-blue-800',
      'pending_review': 'bg-amber-100 text-amber-800',
      'completed': 'bg-emerald-100 text-emerald-800',
      'blocked': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getFileTypeIcon = (type) => {
    const icons = {
      'network_diagram': '🗺️',
      'config': '⚙️',
      'excel': '📊',
      'pdf': '📄',
      'word': '📝'
    };
    return icons[type] || '📁';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                CISO Advisor
              </h1>
              <p className="text-sm text-slate-600 mt-1">Security Posture Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">Last updated: Today, 1:09 PM</span>
              {currentUser && (
                <span className="text-sm text-slate-500">
                  {currentUser.full_name} &mdash; <span className="capitalize">{currentUser.role?.replace('_', ' ')}</span>
                </span>
              )}
              {can('write_assessments') && (
                <button
                  onClick={() => navigate('/upload')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                  Upload Document
                </button>
              )}
              <button
                onClick={logout}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Top Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Current Maturity</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{dashboardData.securityPosture.currentMaturity}</p>
                <p className="text-xs text-slate-500 mt-1">Target: {dashboardData.securityPosture.targetMaturity}</p>
              </div>
              <div className={`w-16 h-16 rounded-full ${getMaturityBg(dashboardData.securityPosture.currentMaturity)} flex items-center justify-center`}>
                <span className={`text-2xl font-bold ${getMaturityColor(dashboardData.securityPosture.currentMaturity)}`}>
                  {dashboardData.securityPosture.currentMaturity}
                </span>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-xs text-emerald-600 font-medium">↗ Improving</span>
              <span className="text-xs text-slate-500">vs last quarter</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Critical Findings</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{dashboardData.riskMetrics.criticalFindings}</p>
                <p className="text-xs text-slate-500 mt-1">Require immediate attention</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-xs text-slate-600">
                +{dashboardData.riskMetrics.highFindings} high priority
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Open Remediations</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{dashboardData.riskMetrics.openRemediations}</p>
                <p className="text-xs text-slate-500 mt-1">In progress</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-xs text-slate-600">
                Avg. {dashboardData.riskMetrics.avgRemediationTime} days to close
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Documents Parsed</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{dashboardData.recentFindings.length}</p>
                <p className="text-xs text-slate-500 mt-1">Last 7 days</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Security Posture & Workflows */}
          <div className="lg:col-span-2 space-y-8">
            {/* Security Posture by Category */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-6">Security Posture by Category</h2>
              
              <div className="space-y-4">
                {dashboardData.securityPosture.categories.map((category, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">{category.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-600">
                          {category.score} / {category.target}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ${
                          category.score >= category.target * 0.9 
                            ? 'bg-emerald-500' 
                            : category.score >= category.target * 0.7 
                            ? 'bg-blue-500' 
                            : category.score >= category.target * 0.5 
                            ? 'bg-amber-500' 
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${(category.score / category.target) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Workflows */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-slate-900">Active Workflows</h2>
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All →</button>
              </div>

              <div className="space-y-4">
                {dashboardData.workflows.map((workflow) => (
                  <div key={workflow.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-slate-900">{workflow.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(workflow.status)}`}>
                            {workflow.status.replace('_', ' ')}
                          </span>
                          <span className="text-xs text-slate-500">Due: {workflow.dueDate}</span>
                        </div>
                      </div>
                      <button className="text-slate-400 hover:text-slate-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Progress</span>
                        <span className="font-medium text-slate-900">{workflow.progress}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${workflow.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Quick Actions & Recent Activity */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
              
              <div className="space-y-3">
                {can('write_assessments') && (
                <button className="w-full flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left group">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Upload Document</div>
                    <div className="text-xs text-slate-600">Network diagrams, configs, policies</div>
                  </div>
                </button>
                )}

                {can('write_assessments') && (
                <button 
                  onClick={() => navigate('/assessment')}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left group"
                >
                  <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Start Assessment</div>
                    <div className="text-xs text-slate-600">15-category capability assessment</div>
                  </div>
                </button>
                )}

                <button 
                  onClick={() => navigate('/gap-analysis')}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left group"
                >
                  <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">View Gap Analysis</div>
                    <div className="text-xs text-slate-600">Current vs target comparison</div>
                  </div>
                </button>

                {/* NEW: Risk Analysis Quick Action */}
                <button 
                  onClick={() => navigate('/risk-analysis')}
                  className="w-full flex items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left group"
                >
                  <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Risk Analysis</div>
                    <div className="text-xs text-slate-600">Quantitative financial risk calculation</div>
                  </div>
                </button>

                <button 
                  onClick={() => navigate('/feedback')}
                  className="w-full flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left group"
                >
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Submit Feedback</div>
                    <div className="text-xs text-slate-600">Report a bug or request a feature</div>
                  </div>
                </button>

                {can('export_reports') && (
                <button className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left group">
                  <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">Generate Report</div>
                    <div className="text-xs text-slate-600">Executive summary &amp; findings</div>
                  </div>
                </button>
                )}
              </div>
            </div>

            {/* Recent Findings */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900">Recent Documents</h2>
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All →</button>
              </div>

              <div className="space-y-3">
                {dashboardData.recentFindings.map((finding) => (
                  <div key={finding.id} className="border border-slate-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getFileTypeIcon(finding.type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm truncate">{finding.name}</div>
                        <div className="text-xs text-slate-500 mt-1">{finding.uploadedAt}</div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                            {finding.status}
                          </span>
                          {finding.devices && (
                            <span className="text-xs text-slate-600">{finding.devices} devices</span>
                          )}
                          {finding.assets && (
                            <span className="text-xs text-slate-600">{finding.assets} assets</span>
                          )}
                          {finding.issues && (
                            <span className="text-xs text-red-600">{finding.issues} issues</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
